import { useEffect, useCallback } from 'react';
import type { AuthError, EmailOtpType, Session } from '@supabase/supabase-js';
import i18n from 'i18next';
// Imported first so the URL error is captured before the Supabase client
// (or a route-guard redirect) gets a chance to rewrite the location.
import '@/shared/lib/auth-url-error';
import { supabase } from '@/shared/lib/supabase';
import { queryClient } from '@/shared/lib/query-client';
import { useAuthStore } from '@/shared/stores/auth.store';
import { useWorkspaceStore } from '@/shared/stores/workspace.store';
import { markSignInTime } from '@/shared/services/api';
import { checkMfaAssurance, resetMfaState, useMfaStore } from '@/shared/hooks/useAuthMfa';
import { useComposerStore } from '@/shared/stores/composer.store';
import { clearUserScopedStorage } from '@/shared/lib/user-scoped-storage';
import { clearPasswordRecovery, markPasswordRecovery } from '@/shared/lib/auth-link-type';

/**
 * Drop everything that belongs to the signed-out user. Without this, signing
 * out and then signing in (or up) as someone else in the same tab kept the
 * previous user's `currentWorkspace` + cached `/workspaces/me`, so every
 * request carried a workspace the new user isn't a member of and 403'd.
 */
export function clearUserScopedState(): void {
  useWorkspaceStore.getState().reset();
  queryClient.clear();
  resetMfaState();
  // The composer's hand-off (draft id, media) and onboarding answers belong
  // to the person signing out, not the next one on this browser.
  const composer = useComposerStore.getState();
  composer.reset();
  composer.setLastSession(null);
  clearUserScopedStorage();
  clearPasswordRecovery();
}

type SetSession = (session: Session | null) => void;
type SetLoading = (loading: boolean) => void;

let authConsumers = 0;
let stopAuthListener: (() => void) | null = null;

/**
 * One session read and one onAuthStateChange subscription for the whole app,
 * however many components call useAuth (15 did, so a sign-out cleared the
 * stores 15 times). Reference-counted: the last consumer to unmount stops it.
 */
function retainAuthListener(setSession: SetSession, setLoading: SetLoading): () => void {
  authConsumers += 1;
  if (authConsumers === 1) {
    supabase.auth
      .getSession()
      .then(({ data: { session: s } }) => {
        setSession(s);
        void checkMfaAssurance(s);
      })
      // A failed read is "signed out", not a skeleton forever.
      .catch(() => setSession(null))
      .finally(() => setLoading(false));

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, s) => {
      // Covers sign-ins that don't go through signInWithEmail — the Google
      // OAuth return and email-confirmation links — so they get the same
      // post-sign-in 401 grace period.
      if (event === 'SIGNED_IN') markSignInTime();
      // Also fires for sign-outs from another tab or a failed token refresh.
      if (event === 'SIGNED_OUT') clearUserScopedState();
      setSession(s);
      // Every sign-in path (password, magic link, Google) and the code step
      // itself (MFA_CHALLENGE_VERIFIED) re-reads the assurance level.
      void checkMfaAssurance(s);
    });
    stopAuthListener = () => subscription.unsubscribe();
  }
  return () => {
    authConsumers -= 1;
    if (authConsumers === 0) {
      stopAuthListener?.();
      stopAuthListener = null;
    }
  };
}

/**
 * Ends every session of this account except this one. Never throws: the
 * password is already changed, and the API also refuses revoked sessions.
 */
export async function signOutOtherSessions(): Promise<void> {
  try {
    await supabase.auth.signOut({ scope: 'others' });
  } catch {
    // Nothing to undo; the Security tab still offers "Sign out other devices".
  }
}

/** The interface language now, stored on a new account for its emails. */
function uiLanguageNow(): string {
  return ((i18n.language || 'en').split('-')[0] ?? 'en').toLowerCase();
}

/**
 * Supabase's answer when a sign-in link is asked for an address with no
 * account and creating one is not allowed: treated as sent, so the form never
 * tells a stranger which addresses have accounts.
 */
function isNoAccountError(error: AuthError): boolean {
  const code = (error as { code?: string }).code;
  return code === 'otp_disabled' || code === 'signup_disabled' || code === 'user_not_found' || /signups? not allowed/i.test(error.message);
}

const LOCAL_ORIGIN = /^https?:\/\/(localhost|127\.0\.0\.1)(:|\/|$)/i;

/**
 * Resolve the app origin the links in auth emails return to.
 *
 * VITE_APP_URL wins, except a localhost value on a page that is not itself on
 * localhost: the build on the server reads the box's own apps/web/.env, and a
 * copy of the local one there put http://localhost:5173 into every
 * confirmation, magic-link and reset email. The page's own origin is used
 * then, and when VITE_APP_URL is unset.
 */
export function resolveAppOrigin(configured: string | undefined, pageOrigin: string | undefined): string {
  const usable = configured && /^https?:\/\//i.test(configured) ? configured.replace(/\/+$/, '') : undefined;
  const page = pageOrigin && /^https?:\/\//i.test(pageOrigin) ? pageOrigin : undefined;
  if (usable && !(LOCAL_ORIGIN.test(usable) && page && !LOCAL_ORIGIN.test(page))) return usable;
  return page ?? 'https://app.unipostudio.com';
}

function getAppOrigin(): string {
  return resolveAppOrigin(
    import.meta.env.VITE_APP_URL,
    typeof window !== 'undefined' ? window.location.origin : undefined,
  );
}

export function useAuth() {
  const { user, session, isLoading: sessionLoading, isAuthenticated: hasSession, setSession, setLoading, reset } =
    useAuthStore();
  const mfaStatus = useMfaStore((s) => s.status);
  // A session at aal1 for someone with a verified factor is not signed in
  // yet: the guards send them to the code step (AuthPage). While a new
  // session's level is being read the app waits rather than flash in.
  const mfaRequired = hasSession && mfaStatus === 'required';
  const isLoading = sessionLoading || (hasSession && (mfaStatus === 'idle' || mfaStatus === 'checking'));
  const isAuthenticated = hasSession && mfaStatus === 'satisfied';

  useEffect(() => retainAuthListener(setSession, setLoading), [setSession, setLoading]);

  const signInWithEmail = useCallback(
    async (email: string, password: string) => {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) return { error, mfaRequired: false };
      markSignInTime();
      const status = await checkMfaAssurance(data?.session ?? null);
      return { error: null, mfaRequired: status === 'required' };
    },
    [],
  );

  const signUpWithEmail = useCallback(
    async (email: string, password: string, name?: string) => {
      const trimmed = name?.trim();
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${getAppOrigin()}/auth`,
          // Persist the display name into user_metadata so the dashboard
          // greeting and team list show it from first sign-in.
          // The language travels with the account, so the confirmation email
          // (our Send Email hook) is already in it.
          data: { ...(trimmed ? { full_name: trimmed, name: trimmed } : {}), ui_language: uiLanguageNow() },
        },
      });
      // With email confirmation off, a taken email is an explicit error.
      if (error?.code === 'user_already_exists') {
        return { error: null, alreadyRegistered: true, signedIn: false };
      }
      if (error) return { error, alreadyRegistered: false, signedIn: false };
      // With email confirmation on, Supabase hides "email already registered"
      // behind a fake success: a user object with no identities and no email
      // sent. Surface it so the user isn't left waiting for a mail that will
      // never arrive.
      const alreadyRegistered = !data?.session && data?.user?.identities?.length === 0;
      // With email confirmation off, sign-up returns a live session.
      const signedIn = !!data?.session;
      if (signedIn) markSignInTime();
      return { error: null, alreadyRegistered, signedIn };
    },
    [],
  );

  // Lets a user whose confirmation mail never arrived (or expired) get a new
  // one — without this an unconfirmed account had no way forward.
  const resendConfirmation = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: { emailRedirectTo: `${getAppOrigin()}/auth` },
    });
    return { error };
  }, []);

  // Passwordless sign-in (S02). Only the sign-up form may create an account
  // (`create`): a typo on the sign-in form used to start a new, empty
  // account (AL-47). Sign-in answers "check your inbox" whether or not the
  // address has an account, so the form never reveals who is a customer. The
  // emailed link returns to /auth, where the SIGNED_IN handler above picks up
  // the session and the guest guard sends the person home.
  //
  // Locally the Supabase stack runs with autoconfirm on and its site URL
  // pointing at the API port, so the link in the mail (or in Inbucket) must
  // be opened on the app origin (http://localhost:5173/auth), not the API's.
  const signInWithMagicLink = useCallback(
    async (email: string, options: { create?: boolean } = {}): Promise<{ error: AuthError | null }> => {
      const create = options.create === true;
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${getAppOrigin()}/auth`,
          shouldCreateUser: create,
          ...(create ? { data: { ui_language: uiLanguageNow() } } : {}),
        },
      });
      if (error && !create && isNoAccountError(error)) return { error: null };
      return { error };
    },
    [],
  );

  // The 6-digit code from a sign-in, sign-up or reset email (M-04), for when
  // the link opens on another device. A reset code lands on the new-password page.
  const verifyEmailCode = useCallback(
    async (email: string, token: string, type: Extract<EmailOtpType, 'email' | 'recovery'>): Promise<{ error: AuthError | null; mfaRequired: boolean }> => {
      const { data, error } = await supabase.auth.verifyOtp({ email, token, type });
      if (error) return { error, mfaRequired: false };
      markSignInTime();
      if (type === 'recovery') markPasswordRecovery();
      const status = await checkMfaAssurance(data?.session ?? null);
      return { error: null, mfaRequired: status === 'required' };
    },
    [],
  );

  const signInWithGoogle = useCallback(async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${getAppOrigin()}/dashboard` },
    });
    return { error };
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${getAppOrigin()}/auth/reset-password`,
    });
    return { error };
  }, []);

  const updatePassword = useCallback(async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    // A new password ends every other session: whoever knew the old one is out (AL-23).
    if (!error) await signOutOtherSessions();
    return { error };
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    clearUserScopedState();
    reset();
  }, [reset]);

  return {
    user,
    session,
    isLoading,
    isAuthenticated,
    /** Signed in at aal1 with a verified factor: the code step is due. */
    mfaRequired,
    /** A Supabase session exists (possibly still awaiting the code step). */
    hasSession,
    signInWithEmail,
    signUpWithEmail,
    resendConfirmation,
    signInWithMagicLink,
    verifyEmailCode,
    signInWithGoogle,
    signOut,
    resetPassword,
    updatePassword,
  };
}
